import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

/**
 * Zentraler Bewerbungsstatus eines Webinars — Spiegel von
 * `node-app/src/helpers/webinarPromotionStatus.ts` (Statuswerte, Hindernis-/Warnungsform).
 * Der Server rechnet, die Oberfläche zeigt nur an.
 */
export type PromoStatus = 'bewerbbar' | 'unvollstaendig' | 'deaktiviert' | 'vorbei';

export interface PromoHindernis {
  code: string;
  text: string;
  /** Wo man es behebt: LP-Manager, Reiter Reminder, oder am Webinar selbst. */
  ziel: 'lp' | 'reminder' | 'webinar' | 'raum';
}

export interface PromoWarnung {
  code: string;
  text: string;
}

/** Farbe + Variante je Status — bewerbbar grün, unvollständig gelb, deaktiviert grau, vorbei umrandet. */
export function promoChipStyle(status: PromoStatus | null | undefined): {
  color: 'success' | 'warning' | 'default';
  variant: 'filled' | 'outlined';
} {
  switch (status) {
    case 'bewerbbar':
      return { color: 'success', variant: 'filled' };
    case 'unvollstaendig':
      return { color: 'warning', variant: 'filled' };
    case 'vorbei':
      return { color: 'default', variant: 'outlined' };
    default:
      return { color: 'default', variant: 'filled' };
  }
}

/** Tooltip-Text: Hindernisse zeilenweise, sonst der Grund für den Endzustand. */
export function promoTooltipLines(
  status: PromoStatus | null | undefined,
  hindernisse: PromoHindernis[] | null | undefined,
): string[] {
  if (hindernisse && hindernisse.length) return hindernisse.map((h) => h.text);
  if (status === 'bewerbbar') return ['Alle Voraussetzungen erfüllt'];
  if (status === 'vorbei') return ['Termin vorbei'];
  if (status === 'deaktiviert') return ['Deaktiviert'];
  return [];
}

interface Props {
  status: PromoStatus | null | undefined;
  label?: string | null;
  hindernisse?: PromoHindernis[] | null;
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
}

const WebinarPromoChip: React.FC<Props> = ({ status, label, hindernisse, size = 'small', sx }) => {
  if (!status) return null;
  const style = promoChipStyle(status);
  const lines = promoTooltipLines(status, hindernisse);
  const chip = (
    <Chip size={size} label={label || status} color={style.color} variant={style.variant} sx={sx} />
  );
  if (!lines.length) return chip;
  return (
    <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{lines.join('\n')}</span>}>
      <span>{chip}</span>
    </Tooltip>
  );
};

export default WebinarPromoChip;
